/**
 * `db` — PostgREST client with a Supabase-like surface.
 *
 * O nome do módulo é `db` porque a maior parte do uso é `db.from(...)` / `db.rpc(...)`.
 * A surface mais ampla (`.storage`, `.functions.invoke`, `.auth`, `.channel`) sobrou
 * da remoção do `@supabase/supabase-js` — mantemos os shims pra não precisar
 * reescrever os ~1100 call sites de uma vez. Todo o tráfego roteia via:
 *
 *   - `.from` / `.rpc` / `.schema`  → `VITE_API_URL/rest/v1` (PostgREST sidecar)
 *   - `.storage`                    → `src/modules/shared/lib/storage-client.ts` (S3)
 *   - `.functions.invoke("name")`   → `VITE_API_URL/functions/v1/<name>` (aliases no Fastify)
 *   - `.auth`                       → stub no-op (use `authClient` do lib/auth-client.ts)
 *   - `.channel` / `.removeChannel` → stubs (não há Realtime)
 */

import { PostgrestClient } from "@supabase/postgrest-js";
import type { Database } from "./db-types";
import { storageClient } from "@/modules/shared/lib/storage-client";
import { getAccessToken, ensureFreshToken, tryRefreshToken } from "@/modules/shared/lib/api-client";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.openhubr.com";
const POSTGREST_URL = `${API_BASE_URL}/rest/v1`;

function buildAuthHeaders(init: RequestInit, includeApiKey: boolean): Headers {
  const headers = new Headers(init.headers);
  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (includeApiKey && !headers.has("apikey")) headers.set("apikey", "openhub");
  return headers;
}

const cognitoFetch: typeof fetch = async (input, init = {}) => {
  await ensureFreshToken();

  let res = await fetch(input as RequestInfo, { ...init, headers: buildAuthHeaders(init, true) });

  if (res.status === 401 && getAccessToken()) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      res = await fetch(input as RequestInfo, { ...init, headers: buildAuthHeaders(init, true) });
    }
  }

  return res;
};

const postgrest = new PostgrestClient<Database>(POSTGREST_URL, {
  fetch: cognitoFetch,
  headers: { apikey: "openhub" },
});

// ─── Functions — maps to POST {API_BASE_URL}/functions/v1/<name> ───────────
// Aliases ficam em api/src/server.ts FUNCTION_ALIASES.

interface InvokeOptions {
  body?: any;
  headers?: Record<string, string>;
}

interface InvokeResult<T = any> {
  data: T | null;
  error: { message: string; status?: number } | null;
}

async function invokeFunction<T = any>(
  name: string,
  options: InvokeOptions = {}
): Promise<InvokeResult<T>> {
  try {
    await ensureFreshToken();

    const url = `${API_BASE_URL}/functions/v1/${name}`;
    const buildHeaders = (): Record<string, string> => {
      const h: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      };
      const token = getAccessToken();
      if (token) h["Authorization"] = `Bearer ${token}`;
      return h;
    };
    const body = options.body !== undefined ? JSON.stringify(options.body) : undefined;

    let res = await fetch(url, { method: "POST", headers: buildHeaders(), body });

    if (res.status === 401 && getAccessToken()) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        res = await fetch(url, { method: "POST", headers: buildHeaders(), body });
      }
    }

    const isJson = (res.headers.get("content-type") || "").includes("application/json");
    const payload = isJson ? await res.json().catch(() => null) : null;

    if (!res.ok) {
      return {
        data: null,
        error: {
          message: (payload && (payload.error || payload.message)) || res.statusText,
          status: res.status,
        },
      };
    }

    return { data: (payload ?? null) as T, error: null };
  } catch (err: any) {
    return { data: null, error: { message: err.message || "Invoke failed" } };
  }
}

// ─── Realtime stub (no-op) ─────────────────────────────────────────────────

interface ChannelStub {
  on: (...args: any[]) => ChannelStub;
  subscribe: (...args: any[]) => ChannelStub;
  unsubscribe: () => void;
}

function noopChannel(): ChannelStub {
  const stub: ChannelStub = {
    on: () => stub,
    subscribe: () => stub,
    unsubscribe: () => {},
  };
  return stub;
}

// ─── Auth stub ─────────────────────────────────────────────────────────────
// Pages migradas usam `authClient`. As legacy (portal/championship) mantêm as
// referências a `db.auth.*` que caem neste stub.

const authStub = {
  async getSession() {
    return { data: { session: null }, error: null };
  },
  async getUser() {
    return { data: { user: null }, error: null };
  },
  async signInWithPassword(_params: { email: string; password: string }) {
    return {
      data: { user: null, session: null },
      error: new Error("Portal/championship auth not implemented on this backend yet"),
    };
  },
  async signUp(_params: any) {
    return {
      data: { user: null, session: null },
      error: new Error("Portal/championship auth not implemented on this backend yet"),
    };
  },
  async signOut(_opts?: any) {
    return { error: null };
  },
  async updateUser(_updates: any) {
    return { data: { user: null }, error: new Error("Use authClient.changePassword") };
  },
  onAuthStateChange(_cb: any) {
    return {
      data: {
        subscription: { unsubscribe: () => {} },
      },
    };
  },
};

// ─── Export ────────────────────────────────────────────────────────────────

export const db = {
  from: postgrest.from.bind(postgrest),
  rpc: postgrest.rpc.bind(postgrest),
  schema: postgrest.schema?.bind(postgrest),
  storage: storageClient,
  auth: authStub,
  functions: { invoke: invokeFunction },
  channel: (_name: string) => noopChannel(),
  removeChannel: (_channel: any) => {},
  removeAllChannels: () => {},
} as any;
